import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiChevronUp,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import axios from "axios";
import { REACT_APP_API_PORT } from "../Api";
import { getDecryptedData } from "../config/crypto";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import moment from "moment";
import { Helmet } from "react-helmet";

const AIPrompt = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [categoryData, setcategoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(45);

  const [showUnlockPopup, setShowUnlockPopup] = useState(false);

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [category, setcategory] = useState("");
  const [tags, settags] = useState("");
  const [meta, setMeta] = useState([]);
  const [sortMode, setSortMode] = useState("Latest");

  const popupTimerRef = useRef(null);
  const popupIntervalRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [promptData, setPromptData] = useState([]);
  const [allTags, setAllTags] = useState([]);

  const getAllTags = async () => {
  try {
    const user = getDecryptedData("user");
    const userId = user?._id || "";

    const res = await axios.get(
      `${REACT_APP_API_PORT}user/homepage/all_instagram_prompts_newpage`,
      {
        params: {
          limit: 1000,
          page: 1,
          userId,
          search: "",
          duration: "All",
          category: "",
          tags: "",
        },
      }
    );

    const prompts = res.data?.data || [];

    const extractedTags = prompts.flatMap((item) => {
      if (!item?.tags) return [];

      const itemTags = Array.isArray(item.tags)
        ? item.tags
        : [item.tags];

      return itemTags.flatMap((tag) =>
        String(tag)
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
      );
    });

    setAllTags([...new Set(extractedTags)]);
  } catch (error) {
    console.error("Error fetching tags:", error);
    setAllTags([]);
  }
};

 const uniqueTags = useMemo(() => {
  const allTags = promptData.flatMap((item) => {
    const itemTags = item?.tags;

    if (!itemTags) return [];

    // Agar tags array hai
    if (Array.isArray(itemTags)) {
      return itemTags.flatMap((tag) =>
        String(tag)
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
      );
    }

    // Agar tags string hai
    return String(itemTags)
      .split(",")
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  });

  return [...new Set(allTags)];
}, [promptData]);


  const getData = async (page = 1) => {
  try {
    setIsLoading(true);

    const user = getDecryptedData("user");
    const userId = user?._id || "";

    const params = {
      limit,
      page,
      userId,
      search: search || "",
      duration: selectedCategory || "All",
      category: category || "",
      tags: tags && tags !== "Premium" ? tags : "",
    };

    const res = await axios.get(
      `${REACT_APP_API_PORT}user/homepage/all_instagram_prompts_newpage`,
      {
        params,
      }
    );

    setData(res.data?.data || []);
    setTotalPages(res.data?.pagination?.totalPages || 1);
    setTotalItems(res.data?.pagination?.totalItems || 0);
  } catch (err) {
    console.error("Error fetching prompts:", err);

    setData([]);
    setTotalPages(1);
    setTotalItems(0);
  } finally {
    setIsLoading(false);
  }
};

  const getCategoryData = async () => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_PORT}user/homepage/activepromptcategory`
      );
      setcategoryData(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMetaData = async () => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_PORT}user/homepage/page-seo-details/Prompts Page`
      );

      if (res.data?.status) setMeta(res.data?.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCategoryData();
    fetchMetaData();
    getAllTags();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, tags, category, selectedCategory]);

  useEffect(() => {
    getData(currentPage);
  }, [currentPage, search, tags, category, selectedCategory]);

  const categoryLabel = (item) =>
    item?.name ||
    item?.category_name ||
    item?.title ||
    item?.category ||
    item?.label ||
    "";

  const sortedData = useMemo(() => {
    const list = [...data];

    if (sortMode === "Oldest") {
      return list.sort(
        (a, b) =>
          new Date(a?.createdAt || 0).getTime() -
          new Date(b?.createdAt || 0).getTime()
      );
    }

    if (sortMode === "Popular") {
      return list.sort(
        (a, b) =>
          Number(
            b?.views ||
              b?.view_count ||
              b?.likes ||
              b?.saved_count ||
              0
          ) -
          Number(
            a?.views ||
              a?.view_count ||
              a?.likes ||
              a?.saved_count ||
              0
          )
      );
    }

    return list.sort(
      (a, b) =>
        new Date(b?.createdAt || 0).getTime() -
        new Date(a?.createdAt || 0).getTime()
    );
  }, [data, sortMode]);

  const stripHtml = (html = "") => html.replace(/<[^>]+>/g, "");

  const copyPrompt = async (item) => {
    const prompts = [];


    if (!prompts.length) {
      toast.info("No prompt available");
      return;
    }

    try {
      await navigator.clipboard.writeText(prompts.join("\n"));
      toast.success("Prompt copied");
    } catch {
      toast.error("Could not copy prompt");
    }
  };



  return (
    <>
      <Helmet>
        <title>{meta?.meta_title || "Satyam AI Prompts"}</title>
        <meta
          name="description"
          content={meta?.meta_description || "AI prompts curated by Satyam AI"}
        />
        <meta name="keywords" content={meta?.meta_keywords || "AI prompts"} />
      </Helmet>

      <Loader isLoading={isLoading} />

      {/* New minimal header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#111111]/95 backdrop-blur">
        <div className="mx-auto flex h-[64px] max-w-[1180px] items-center justify-between px-5 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#202020] text-sm font-bold text-white">
              S
            </div>
            <span className="text-[17px] font-semibold tracking-[-0.02em] text-white">
              Satyam AI
            </span>
          </div>

          <a
            href="https://www.instagram.com/nextfutureai/"
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-white transition hover:text-white"
          >
            Instagram
          </a>
        </div>
      </header>

      <main className="min-h-screen bg-[#111111] text-white">
        <div className="mx-auto max-w-[1180px] px-5 pb-16 pt-14 sm:px-6 sm:pt-20 lg:pt-24">
          {/* Hero */}
          <section className="max-w-[660px]">
            <div className="mb-5 inline-flex items-center rounded-full border border-[#e8d4ca] bg-[#f9eee9] px-3 py-1 text-[11px] font-semibold text-[#b4694f]">
              • Free for everyone
            </div>

            <h1 className="font-serif text-[48px] font-semibold leading-[0.98] tracking-[-0.045em] text-white sm:text-[64px] lg:text-[72px]">
             One place for
              <br />
              better AI prompts
            </h1>

           <p className="mt-6 text-[16px] leading-relaxed text-white/70 sm:text-[17px]">
            Explore powerful AI prompts for{" "}
            <span className="font-semibold text-white">ChatGPT, Claude, Gemini, Google Flow</span>{" "}
            and more — all in one place.
            </p>
          </section>

          {/* Search + sorting */}
          <section className="mt-16 sm:mt-20">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full max-w-[390px]">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[#929292]" />
                <input
                  type="text"
                  placeholder="Search prompts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-[50px] w-full rounded-[14px] border border-[#dededb] bg-white pl-11 pr-4 text-[15px] text-[#333] outline-none transition placeholder:text-[#a3a3a3] focus:border-[#aaa] focus:ring-2 focus:ring-black/[0.04]"
                />
              </div>

              <div className="flex w-fit rounded-[12px] bg-[#f1f1ef] p-1">
                {["Latest", "Popular", "Oldest"].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setSortMode(item)}
                    className={`rounded-[9px] px-4 py-2 text-[13px] font-medium transition ${
                      sortMode === item
                        ? "bg-white text-[#2a2a2a] shadow-sm"
                        : "text-[#777] hover:text-[#333]"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter pills */}
         <div className="mt-6 flex flex-wrap gap-2">

  {/* ALL */}
  <button
    type="button"
    onClick={() => {
      setSelectedCategory("All");
      setcategory("");
      settags("");
      setCurrentPage(1);
    }}
    className={`rounded-full border px-4 py-2 text-[13px] font-medium transition ${
      !tags
        ? "border-white bg-white text-black"
        : "border-white/20 bg-transparent text-white/70 hover:border-white/50"
    }`}
  >
    All
  </button>

  {/* ALL TAGS */}
  {allTags.map((tag) => {
    const isActive = tags?.toLowerCase() === tag.toLowerCase();

    return (
      <button
        key={tag}
        type="button"
        onClick={() => {
          setSelectedCategory("All");
          setcategory("");
          settags(tag);
          setCurrentPage(1);
        }}
        className={`rounded-full border px-4 py-2 text-[13px] font-medium transition ${
          isActive
            ? "border-white bg-white text-black"
            : "border-white/20 bg-transparent text-white/70 hover:border-white/50 hover:text-white"
        }`}
      >
        {tag}
      </button>
    );
  })}

</div>

            <div className="mt-7 text-[13px] text-[#777]">
              {totalItems} prompts
            </div>
          </section>

          {/* Prompt cards */}
          <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            {sortedData.map((item) => (
              <article
      key={item._id}
      onClick={() =>
        navigate(`/ai-prompts-detail/${item._id}`)
      }
      className="group relative min-h-[126px] cursor-pointer rounded-[18px] border border-[#dfdfdc] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(30,30,20,0.035)] transition duration-200 hover:-translate-y-[2px] hover:border-[#cfcfca] hover:shadow-[0_14px_32px_rgba(30,30,20,0.07)]"
    > 
     <div className="absolute right-[70px] top-5 text-right">
  <div className="text-[11px] font-medium text-[#555]">
    {item?.date
      ? moment(item.date).format("DD MMM YYYY")
      : ""}
  </div>

  <div className="mt-0.5 text-[9px] font-medium uppercase tracking-[0.05em] text-[#999]">
    {item?.date
      ? moment(item.date).fromNow()
      : ""}
  </div>
</div>
                <div className="pr-14">
                  <div className="flex flex-wrap gap-2">
                    {item?.tags?.[0] && (
                      <span className="rounded-full bg-[#252525] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-white">
                        {item.tags[0]}
                      </span>
                    )}

                    {(item?.prompt_type || item?.category_name) && (
                      <span className="rounded-full bg-[#f0f0ee] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#777]">
                        {item.prompt_type || item.category_name}
                      </span>
                    )}

                   
                  </div>

                  <h2 className="mt-4 line-clamp-1 text-[16px] font-semibold tracking-[-0.02em] text-[#343434]">
                    {item.title}
                  </h2>

                  <p className="mt-2 line-clamp-1 text-[13px] text-[#8b8b8b]">
                    {stripHtml(item.short_description) || "Explore this AI prompt"}
                  </p>
                </div>

                <div className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-[13px] border border-[#e1e1de] bg-[#fafaf9] text-[#8b8b8b] transition group-hover:border-[#cfcfca] group-hover:text-[#333]">
                  <FiChevronRight className="text-xl" />
                </div>
              </article>
            ))}
          </section>

          {!isLoading && sortedData.length === 0 && (
            <div className="mt-8 rounded-[18px] border border-[#dfdfdc] bg-white px-6 py-16 text-center">
              <h3 className="text-lg font-semibold text-[#333]">
                No prompts found
              </h3>
              <p className="mt-2 text-sm text-[#888]">
                Try another search or category.
              </p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="rounded-xl border border-[#dededb] bg-white px-5 py-2.5 text-sm font-medium text-black transition hover:border-[#bbb] disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Prev
              </button>

              <span className="text-sm text-white">
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="rounded-xl border border-[#dededb] bg-white px-5 py-2.5 text-sm font-medium text-black transition hover:border-[#bbb] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default AIPrompt;